import { useCallback } from 'react'

import { F0Box, F0Link, F0TagAlert, F0Text } from '@factorialco/f0-react'

import { IntegrationsAgreggatedSyncableStateValuesEnum } from 'generated/resources/graphql'

import { usePrivateIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'
import { useSyncBarData, type SyncBarStatus } from 'modules/integrations/hooks/useSyncBarData'
import type { SyncFilterDefinition } from 'modules/integrations/lib/syncFilters'

import { SyncBarAction } from './SyncBarAction'

const getSyncStatusTagLevel = (
  status: SyncBarStatus
): 'positive' | 'critical' | 'warning' | 'info' => {
  switch (status) {
    case 'pending':
      return 'info'
    case 'syncing':
      return 'info'
    case 'failed':
      return 'critical'
    case 'synced':
      return 'positive'
  }
}

interface SyncBarProps {
  syncFilters?: SyncFilterDefinition[]
  skipConfirmationIfNoIssues?: boolean
  disabled?: boolean
  tooltip?: string
}

export const SyncBar = ({
  syncFilters = [],
  skipConfirmationIfNoIssues = false,
  disabled = false,
  tooltip,
}: SyncBarProps) => {
  const { dataCollectionSourceRef, marketplaceIntegration, isBookkeeperView } =
    usePrivateIntegrationsSyncContext()
  const { syncStatus, syncStatusLabel, statusCounts, isExportMode } = useSyncBarData()

  const applyStatusFilter = useCallback(
    (status: IntegrationsAgreggatedSyncableStateValuesEnum) => {
      const source = dataCollectionSourceRef.current
      if (source) {
        const setFilters = source.setCurrentFilters
        if (typeof setFilters === 'function') {
          setFilters({ integrationsAgreggatedSyncableStates: [status] })
        }
      }
    },
    [dataCollectionSourceRef]
  )

  if (!syncStatus || !syncStatusLabel || !marketplaceIntegration?.displayName) return null

  const level = getSyncStatusTagLevel(syncStatus)

  return (
    <F0Box
      display="flex"
      flexDirection="row"
      alignItems="center"
      justifyContent="between"
      gap="md"
      paddingLeft="lg"
      paddingRight={isBookkeeperView ? 'lg' : '5xl'}
      paddingTop="lg"
      paddingBottom={isBookkeeperView ? 'lg' : 'xs'}
      marginBottom="lg"
      borderTop="default"
      borderBottom={isBookkeeperView ? 'default' : undefined}
      borderColor="secondary"
    >
      <F0Box display="flex" flexDirection="row" alignItems="center" gap="md">
        <F0TagAlert text={syncStatusLabel} level={level} />
        {statusCounts.length > 0 && (
          <F0Box display="flex" flexDirection="row" alignItems="center" gap="md" paddingX="sm">
            {statusCounts.map((counter, index) => (
              <F0Box
                key={counter.status}
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="md"
              >
                {index > 0 && <F0Text variant="description" markdown={false} content="|" />}
                {isBookkeeperView ? (
                  <F0Text
                    variant="description"
                    markdown={false}
                    content={`${counter.count} ${counter.label}`}
                  />
                ) : (
                  <F0Link variant="link" onClick={() => applyStatusFilter(counter.status)}>
                    {`${counter.count} ${counter.label}`}
                  </F0Link>
                )}
              </F0Box>
            ))}
          </F0Box>
        )}
      </F0Box>
      <SyncBarAction
        syncStatus={syncStatus}
        integrationName={marketplaceIntegration.displayName}
        isExportMode={isExportMode}
        syncFilters={syncFilters}
        skipConfirmationIfNoIssues={skipConfirmationIfNoIssues}
        disabled={disabled}
        tooltip={tooltip}
      />
    </F0Box>
  )
}
